import { type } from 'arktype';
import type { AddressResponse } from './address.model';

const MAX_PROFILE_IMAGE_DATA_URI_LENGTH = 1_400_000;

// ISO 3166-1 alpha-2 country code validation (uppercase 2-letter codes)
const isoCountryCodeSchema = type('string').narrow(
  (s, ctx) => /^[A-Z]{2}$/.test(s) || ctx.mustBe('a valid ISO 3166-1 alpha-2 country code (e.g., "CH", "DE", "US")')
);

/**
 * Address schema for person records.
 */
export const addressSchema = type({
  street: type('string').configure({ description: 'Street line, including house number.', examples: ['Bahnhofstrasse 10'] }),
  city: type('string').configure({ examples: ['Zürich'] }),
  postalCode: type('string').configure({ description: 'Postal / ZIP code as a string (preserves leading zeros).', examples: ['8001'] }),
  country: isoCountryCodeSchema.configure({ description: 'ISO 3166-1 alpha-2 country code.', examples: ['CH'] }),
}).configure({ description: 'Postal address attached to a person. All fields are required if an address is supplied.' });

export type PersonAddress = typeof addressSchema.t;

/**
 * Contact entry schema for person records.
 */
export const contactSchema = type({
  type: type("'email' | 'phone'").configure({ description: 'Channel of the contact entry.' }),
  value: type('string').configure({
    description: 'The contact value (email address or phone number in E.164 format).',
    examples: ['jane.doe@example.com'],
  }),
  'label?': type('string').configure({ description: 'Optional human-readable label for this entry.', examples: ['Work'] }),
}).configure({ description: 'One contact entry belonging to a person. The first email is the primary email.' });

export type PersonContact = typeof contactSchema.t;

/**
 * Sex field validation.
 */
export const sexSchema = type("'male' | 'female' | 'other' | 'unspecified'");

export type PersonSex = typeof sexSchema.t;

/**
 * Nationality validation (ISO 3166-1 alpha-2).
 */
export const nationalitySchema = isoCountryCodeSchema;

export const personQuerySchema = type({
  'firstName?': 'string',
  'lastName?': 'string',
  'userId?': 'string',
  /**
   * Search across all person properties with OR logic.
   * Matches: firstName, lastName, contacts, jobRole name, department name, team names, skill names.
   */
  'search?': 'string',
  /**
   * Exclude specific person IDs from results.
   * Accepts comma-separated list of IDs.
   * Useful for breadcrumb sibling navigation (exclude current person).
   */
  'exclude?': 'string',
});

export type PersonQuery = typeof personQuerySchema.t;

export const personDataSchema = type({
  'customProperties?': type('Record<string, unknown>').configure({
    description:
      'Custom-property values keyed by the definition `key`. Values are validated against the tenant custom-property definitions for `person` entities.',
    examples: [{ employeeCategory: 'full-time', costCenter: 'CC-1042' }],
  }),
}).configure({
  description: 'Extensible person data payload.',
  examples: [{ customProperties: { employeeCategory: 'full-time', costCenter: 'CC-1042' } }],
});

export type PersonData = typeof personDataSchema.t;

export const createPersonSchema = type({
  firstName: type('string').configure({ description: 'Given name.', examples: ['Jane'] }),
  lastName: type('string').configure({ description: 'Family name.', examples: ['Doe'] }),
  'jobRoleId?': type('string | null').configure({
    description: 'ID of the job role assigned to the person. `null` clears the assignment.',
    examples: ['jr_pflegefachfrau'],
  }),
  'userId?': type('string').configure({
    description: 'Optional link to the authenticated user this person represents. Set when the person logs into the app.',
    examples: ['auth_abc123'],
  }),
  'address?': addressSchema,
  'contacts?': contactSchema.array().configure({
    description: "Contact entries (email, phone). The first email is treated as the person's primary email.",
  }),
  'dateOfBirth?': type('string').configure({
    description: 'ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['1988-04-12'],
  }),
  'nationality?': nationalitySchema.configure({ description: 'ISO 3166-1 alpha-2 country code.', examples: ['CH'] }),
  'sex?': sexSchema.configure({ description: "Biological sex. Use 'unspecified' when the person has opted out." }),
  'profilePicture?': type(`string <= ${MAX_PROFILE_IMAGE_DATA_URI_LENGTH} | null`).configure({
    description:
      'URL or base64-encoded data URI for the profile image. Use `null` to clear an existing picture. Max 1 MB raw image size (~1.4 million characters as a base64 data URI).',
  }),
  'employeeNumber?': type('string')
    .narrow((s, ctx) => s.length <= 128 || ctx.mustBe('at most 128 characters'))
    .narrow((s, ctx) => /^[A-Za-z0-9_-]+$/.test(s) || ctx.mustBe('alphanumeric (letters, digits, hyphens, or underscores only)'))
    .configure({
      description: 'Unique employee identifier. Auto-generated from the tenant pattern if omitted.',
      examples: ['EMP-2026-00001'],
    }),
  'data?': personDataSchema,
}).configure({
  description: 'Payload for creating a new person record.',
  examples: [{ firstName: 'Jane', lastName: 'Doe', data: { customProperties: { employeeCategory: 'full-time', costCenter: 'CC-1042' } } }],
});

export type CreatePerson = typeof createPersonSchema.t;

export const updatePersonSchema = type({
  'firstName?': type('string').configure({ description: 'Given name.', examples: ['Jane'] }),
  'lastName?': type('string').configure({ description: 'Family name.', examples: ['Doe'] }),
  'jobRoleId?': type('string | null').configure({
    description: 'Updated job role ID. `null` clears the assignment.',
    examples: ['jr_pflegefachfrau'],
  }),
  'userId?': 'string',
  'address?': addressSchema,
  'contacts?': contactSchema.array(),
  'dateOfBirth?': type('string').configure({ description: 'ISO 8601 calendar date (YYYY-MM-DD).', examples: ['1988-04-12'] }),
  'nationality?': nationalitySchema,
  'sex?': sexSchema,
  'profilePicture?': type(`string <= ${MAX_PROFILE_IMAGE_DATA_URI_LENGTH} | null`).configure({
    description:
      'Profile image URL / data URI. `null` clears the existing picture. Max 1 MB raw image size (~1.4 million characters as a base64 data URI).',
  }),
  'employeeNumber?': type('string | null')
    .narrow((s, ctx) => s === null || s.length <= 128 || ctx.mustBe('at most 128 characters'))
    .narrow(
      (s, ctx) => s === null || /^[A-Za-z0-9_-]+$/.test(s) || ctx.mustBe('alphanumeric (letters, digits, hyphens, or underscores only)')
    )
    .configure({
      description: 'Unique employee identifier. Pass `null` to clear.',
      examples: ['EMP-2026-00001'],
    }),
  'data?': personDataSchema,
}).configure({
  description: 'Partial update — fields omitted from the body remain unchanged.',
  examples: [{ data: { customProperties: { employeeCategory: 'part-time' } } }],
});

export type UpdatePerson = typeof updatePersonSchema.t;

/**
 * Planning domain data that can be attached to a person response.
 *
 * Note: Skills and teams are now linked to persons directly, not through employees.
 * Job role and department are also on the persons table now.
 * A person may exist without being an employee (employeeId can be null).
 */
export interface PlanningPersonData {
  /** Employee ID if the person is an employee, null otherwise */
  employeeId: string | null;
  jobRole: { id: string; name: string } | null;
  department: { id: string; name: string } | null;
  teams: Array<{ id: string; name: string }>;
  skills: Array<{
    id: string;
    name: string;
    rating: number | null;
    ratingConfig: { type: string; scale?: number } | null;
  }>;
}

/**
 * Person location assignment data that can be attached to a person response.
 */
export interface PersonLocationData {
  id: string;
  type: 'primary' | 'secondary' | 'home_office';
  locationId: string;
  locationType: 'physical' | 'virtual';
  name: string;
  description: string | null;
  addressId: string | null;
  address?: AddressResponse | null;
  url: string | null;
  customProperties?: Record<string, unknown>;
}

/**
 * Person response with optional domain-specific extensions.
 * Each domain module can add its own property via the PersonEnricher pattern.
 */
export interface PersonResponse {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  userId: string | null;
  // Department is always derived via active team memberships
  // (person_teams → teams.departmentId); no direct FK on persons.
  department: { id: string; name: string } | null;
  jobRoleId: string | null;
  // Extended personal information
  address: PersonAddress | null;
  contacts: PersonContact[] | null;
  dateOfBirth: string | null; // ISO date string (YYYY-MM-DD)
  nationality: string | null; // ISO 3166-1 alpha-2
  sex: PersonSex | null;
  profilePicture: string | null;
  profilePictureFileId: string | null;
  employeeNumber?: string | null;
  /** Per-person custom property values surfaced from `persons.data.customProperties`. */
  customProperties?: Record<string, unknown>;
  deletedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
  // Domain-specific extensions (added by enrichers)
  planning?: PlanningPersonData;
  locations?: PersonLocationData[];
  // Future domains can be added here without modifying core code
  [domain: string]: unknown;
}
